import React from 'react';
import PropTypes from 'prop-types';
import { FaInfoCircle, FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './InfoNotice.scss';

/**
 * InfoNotice Component
 *
 * A reusable notification banner for displaying informational messages,
 * warnings, success messages, or errors.
 *
 * @example
 * <InfoNotice variant="warning">
 *   <strong>93 duplicate groups</strong> in this mailshot. Merge duplicates from the Properties page.
 * </InfoNotice>
 *
 * @example
 * <InfoNotice variant="success">
 *   All duplicates have been merged successfully!
 * </InfoNotice>
 *
 * @example
 * <InfoNotice variant="info" icon={false}>
 *   This is a custom info message without an icon.
 * </InfoNotice>
 */
const InfoNotice = ({
  variant = 'info',
  icon = true,
  customIcon = null,
  children,
  className = '',
  style = {},
  onClose = null,
  fadeOut = false,
  fadeOutDelay = 5000,
  ...rest
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  // Auto fade out if specified
  React.useEffect(() => {
    if (fadeOut && fadeOutDelay > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, fadeOutDelay);

      return () => clearTimeout(timer);
    }
  }, [fadeOut, fadeOutDelay]);

  // Don't render if faded out
  if (!isVisible) {
    return null;
  }

  // Default icons for each variant
  const defaultIcons = {
    info: FaInfoCircle,
    warning: FaExclamationTriangle,
    success: FaCheckCircle,
    error: FaTimesCircle,
  };

  const IconComponent = customIcon || defaultIcons[variant];

  const classes = [
    'info-notice',
    `info-notice--${variant}`,
    fadeOut && !isVisible ? 'info-notice--fade-out' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={style} {...rest}>
      {icon && IconComponent && (
        <IconComponent className="info-notice__icon" />
      )}
      <div className="info-notice__content">
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          className="info-notice__close"
          onClick={onClose}
          aria-label="Close notification"
        >
          <FaTimesCircle />
        </button>
      )}
    </div>
  );
};

InfoNotice.propTypes = {
  /** Variant of the notice (info, warning, success, error) */
  variant: PropTypes.oneOf(['info', 'warning', 'success', 'error']),
  /** Whether to show the default icon for the variant */
  icon: PropTypes.bool,
  /** Custom icon component to override the default */
  customIcon: PropTypes.elementType,
  /** Content of the notice */
  children: PropTypes.node.isRequired,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
  /** Callback for close button (if provided, shows close button) */
  onClose: PropTypes.func,
  /** Whether to automatically fade out */
  fadeOut: PropTypes.bool,
  /** Delay before fading out (milliseconds) */
  fadeOutDelay: PropTypes.number,
};

export default InfoNotice;
